<template>
  <base-modal
    class="z-20"
    title="Certification à distance"
    @close="$emit('close')"
  >
    <div class="bg-neutral p-3 mb-2">
      <p class="text-primary">
        Certifiez l'identité de cet utilisateur pour lui permettre d'accéder aux ordonnances et données de santé des
        ses ayants droits et lui même sur son application Apodis.
      </p>
      <div class="flex justify-start py-2">
        <div class="flex flex-col gap-1">
          <span class="font-bold">Utilisateur demandant l'accès</span>
          <div class="flex flex-row items-center gap-2">
            <Icon name="mdi-cellphone" :size="35" />
            <div class="flex flex-col">
              <p>
                <span class="font-semibold">{{ customerName }}</span><span v-if="user.birth_date"> (né le {{
                  $filters.date(user.birth_date, 'short')
                }})</span>
              </p>
              <span v-if="user.phone">{{ $filters.phoneFormat(user.phone) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-orange-light rounded p-3">
      <p>
        Vérifiez que l'identité de l'utilisateur corresponde bien à un patient réel de votre officine avant de certifier
        son accès.
      </p>
    </div>
    <h2 class="pt-4">
      Sélectionnez le patient correspondant
    </h2>
    <search-input
      v-model="keyword"
      class="mt-3 mb-1"
      :is-loading="isLoading"
      :value="keyword"
      @input="search"
    />
    <div v-if="results.length">
      <div
        v-for="(result, index) in results"
        :key="index"
        class="rounded-lg"
      >
        <span
          v-if="user?.phone && result.information.phone && isSamePhoneNumber(user.phone, result.information.phone)"
          class="text-primary p-2"
        >
          Ce patient a le même numéro que {{ customerName }}
        </span>
        <div
          class="grid grid-cols-2 mx-auto text-sm p-2 border-b-1 rounded-lg align-center hover:bg-purple-200 cursor-pointer"
          :class="[user?.phone && result.information.phone && isSamePhoneNumber(user.phone, result.information.phone) ? 'border-2 border-primary' : '']"
        >
          <div class="flex flex-row gap-2">
            <Icon name="mdi-account-circle" :size="40" class="self-center" />
            <div class="flex flex-col p-4">
              <p>
                <span class="font-bold">{{ result.information.name }}</span>
                <span> (Né le {{ $filters.date(result.information.birth_date, 'short') }})</span>
              </p>
              <span v-if="result.information.phone">{{ $filters.phoneFormat(result.information.phone) }}</span>
            </div>
          </div>
          <div class="flex justify-end">
            <base-button
              :primary="result.status === 'none' || result.status === 'pending'"
              @click="result.status === 'validated' || result.status === 'pre-certified' ? cancel(result) : certifyPhone(result)"
            >
              {{
                result.status === 'validated' || result.status === 'pre-certified' ? 'Annuler la certification' : 'Certifier l\'accès'
              }}
            </base-button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="p-4"
    >
      <span v-if="isLoading">Recherche en cours ...</span>
      <span v-else>Aucun patient ne correspond à votre recherche</span>
    </div>
  </base-modal>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import BaseModal from '@/core/components/base/BaseModal.vue';
import SearchInput from '@/core/components/search-bar/SearchInput.vue';
import {customerCertificationApi, customerApi} from '@/container';
import BaseButton from '@/core/components/base/BaseButton.vue';
import formatHelper from '@/core/helpers/formatHelper';
import Customer from "@/customer/interfaces/Customer";
import {useToastStore} from "@/stores/toast.store";
import Icon from "@/core/components/icon/Icon.vue";
import {User} from "@/core/interfaces/User";

const {user} = defineProps<{ user: User }>();
defineEmits(['close']);

const isLoading = ref<boolean>(true);
const certificationLoading = ref<boolean>(false);
const canceling = ref<boolean>(false);
const results = ref<Customer[]>([]);
const timer = ref<NodeJS.Timeout | null>(null);
const keyword = ref<string>(`${user.first_name} ${user.last_name}`);
const {showSuccess, showError} = useToastStore();

const customerName = computed(() => {
    return `${user.first_name} ${user.last_name}`;
});

onMounted(() => {
    search();
});

/**
 * Compare if two phone number are identical.
 *
 * @param phone1
 * @param phone2
 */
const isSamePhoneNumber = (phone1: string, phone2: string) => {
    return formatHelper.readablePhone(phone1) === formatHelper.readablePhone(phone2);
};

/**
 * Search a customer based on provided keyword.
 */
const search = () => {
    if (timer.value) {
        clearTimeout(timer.value);
    }

    if (keyword.value.length > 3) {
        timer.value = setTimeout(() => {
            isLoading.value = true;

            customerApi().search(keyword.value)
                .catch(() => [])
                .then(customers => {
                    results.value = customers;
                    isLoading.value = false;
                });
        }, 500);
    }
};

/**
 * Certify a phone number for the current customer.
 *
 * @param customer The customer.
 */
const certifyPhone = (customer: Customer) => {
    if (!customer.information.phone || !formatHelper.phoneIsValid(customer.information.phone)) {
        showError({content: 'Le numéro de ce patient n\'est pas valide'});
        return;
    }

    certificationLoading.value = true;

    return customerCertificationApi()
        .certifyUserWithPhone(customer.id, user.id, formatHelper.apiPhone(customer.information.phone))
        .then(() => {
            search();
            showSuccess({content: 'Le patient a été certifié avec succès'});
        })
        .catch(() => {
            showError({content: 'La certification n\'a pas fonctionné, vérifier que le numéro n\'est pas déjà associé à un patient'});
        })
        .finally(() => {
            certificationLoading.value = false;
        });
};

/**
 * Cancel a certification.
 *
 * @param customer
 */
const cancel = (customer: Customer) => {
    canceling.value = true;

    return customerCertificationApi()
        .cancel(customer.id)
        .then(() => {
            search();
        })
        .finally(() => {
            canceling.value = false;
        });
};

</script>
